import classNames from 'classnames'
import { type FC, type ReactNode, useMemo, useRef, useState } from 'react'
import {
  type IChipProps,
  Chip,
  Dropdown,
  type IDropdownItem,
  type IDropdownProps,
  Typography,
} from 'shared/ui'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { useElementWidth } from 'shared/hooks/useElementWidth'
import { useDynamicVisibleChips } from 'shared/ui/Select/hooks/useDynamicVisibleChips'
import styles from '../styles.module.scss'
import { getDisplayMode, type SelectMultiListDisplayMode } from './constants'

export type ISelectMultiListProps = {
  type: 'multiList'
  text: ReactNode
  selectedItems?: IDropdownItem[]
  items: IDropdownItem[]
  dropdownProps?: IDropdownProps
  fullWidth?: boolean
  onDelete?: (item: IDropdownItem) => void
  onSelect?: (items: IDropdownItem) => void
  ChipComponent?: FC<IChipProps>
  withInitialSearch?: boolean
  initialSearchPlaceholder?: string
  parentsDropdownId?: string[]
  customComponent?: IDropdownProps['customComponent']
  error?: ReactNode
  placeholderClassName?: string
  maxVisibleChips?: number
  maxChipsHeight?: number
  displayMode?: SelectMultiListDisplayMode
}

export const SelectMultiList = ({
  text,
  dropdownProps,
  items,
  selectedItems,
  fullWidth,
  onDelete,
  onSelect,
  ChipComponent = Chip,
  withInitialSearch,
  initialSearchPlaceholder = 'Select',
  parentsDropdownId,
  customComponent,
  error,
  maxVisibleChips,
  maxChipsHeight,
  placeholderClassName,
}: ISelectMultiListProps) => {
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLLabelElement | null>(null)
  const innerPlaceholderWrapRef = useRef<HTMLDivElement>(null)
  const triggerWidth = useElementWidth(ref)
  const displayMode = getDisplayMode(maxVisibleChips, maxChipsHeight)

  const dynamicChips = useDynamicVisibleChips(
    ref,
    innerPlaceholderWrapRef,
    selectedItems,
    displayMode === 'singleRow' ? maxVisibleChips : undefined
  )

  const totalCount = selectedItems?.length ?? 0
  const visibleCount =
    displayMode === 'multiRow'
      ? Math.min(maxVisibleChips ?? 0, totalCount)
      : displayMode === 'singleRow'
      ? dynamicChips.visibleCount
      : totalCount
  const hiddenCount =
    displayMode === 'multiRow'
      ? totalCount - visibleCount
      : displayMode === 'singleRow'
      ? dynamicChips.hiddenCount
      : 0

  const visibleItems = useMemo(
    () => (selectedItems ?? []).slice(0, visibleCount),
    [selectedItems, visibleCount]
  )

  const selectedIds = useMemo(
    () => new Set(selectedItems?.map((item) => item.id) || []),
    [selectedItems]
  )

  const showItems = useMemo(() => {
    const lowercasedSearch = search.toLowerCase()
    return items
      .filter(
        (item) =>
          !selectedIds.has(item.id) &&
          (!search || item.label.toLowerCase().includes(lowercasedSearch))
      )
      .map((item) => ({ ...item, disabledClosedTooltip: true }))
  }, [items, search, selectedIds])

  const wrapStyle = displayMode === 'multiRow' ? { maxHeight: maxChipsHeight } : undefined

  const renderChip = (item: IDropdownItem, showDelete?: boolean, index?: number) => {
    const isSingleRow = displayMode === 'singleRow'
    const isHidden = !showDelete && isSingleRow && index && index >= visibleCount
    const attachRef = !showDelete && isSingleRow

    return (
      <div
        key={item.id}
        className={classNames(styles.chipWrapper, {
          [styles.hiddenChip]: isHidden,
        })}
        ref={attachRef ? (el) => el && dynamicChips.chipRefs.current.set(item.id, el) : undefined}
      >
        <ChipComponent
          item={item}
          label={item.label}
          iconType='div'
          onDelete={showDelete ? () => onDelete?.(item) : undefined}
        />
      </div>
    )
  }

  const dropdownWidth = !triggerWidth
    ? undefined
    : displayMode === 'singleRow'
    ? triggerWidth + 8
    : triggerWidth

  return (
    <div className={styles.placeholderWrap}>
      <Dropdown
        ariaLabel={'SelectMultiList'}
        width={fullWidth ? dropdownWidth : undefined}
        disableStopPropagation
        triggerComponent={(open) => (
          <TextFieldPlaceholder
            multiline
            className={placeholderClassName}
            variant='stroke'
            size='medium'
            ref={ref}
            rightActions={[makeTextFieldIcon({ icon: 'chevronDown' })]}
            error={error}
            placeholder={
              selectedItems?.length ? (
                <div className={styles.multiList}>
                  <div
                    className={classNames(styles.placeholderWrap, {
                      [styles.placeholderWrapClosed]: !open && displayMode === 'singleRow',
                      [styles.placeholderWrapWithMaxHeight]: displayMode === 'multiRow',
                    })}
                    ref={innerPlaceholderWrapRef}
                    style={wrapStyle}
                  >
                    {open ? (
                      <>
                        {selectedItems?.map((item) => renderChip(item, true))}
                        <input
                          type='text'
                          className={styles.searchInput}
                          placeholder='Search'
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </>
                    ) : displayMode === 'singleRow' ? (
                      <>
                        {selectedItems?.map((item, index) => renderChip(item, false, index))}
                        {hiddenCount > 0 && (
                          <div
                            className={styles.remainderCount}
                            ref={dynamicChips.remainderCountRef}
                          >
                            +{hiddenCount}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {visibleItems.map((item) => renderChip(item))}
                        {hiddenCount > 0 && (
                          <div className={styles.remainderCount}>+{hiddenCount}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : withInitialSearch ? (
                <input
                  style={{ width: '100%' }}
                  type='text'
                  className={styles.searchInput}
                  placeholder={open ? 'Search' : initialSearchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              ) : (
                <Typography
                  ariaLabel='InitialText'
                  variant={'text-input-field-r'}
                  color='var(--content-neutral-primary)'
                >
                  {text || 'Select'}
                </Typography>
              )
            }
            {...dropdownProps?.textFieldProps}
          />
        )}
        customComponent={customComponent}
        maxHeight={300}
        items={showItems}
        onChange={(item: IDropdownItem) => {
          onSelect?.(item)
          setSearch('')
        }}
        placement={'bottom-start'}
        parentsDropdownId={parentsDropdownId}
        {...dropdownProps}
      />
    </div>
  )
}
